import { api } from './api';
import type {
  ApiResponse,
  SimulationState,
  SimulationEventData,
  SimulationCommand,
} from '../types/simulation.types';

/**
 * Start a simulation for a daily action plan
 */
export const startSimulation = async (planId: string): Promise<SimulationState> => {
  const response = await api.post<ApiResponse<SimulationState>>('/api/simulation/start', {
    planId,
  });
  return response.data.data;
};

/**
 * Stop a running simulation
 */
export const stopSimulation = async (planId: string): Promise<void> => {
  await api.post('/api/simulation/stop', { planId });
};

/**
 * Pause a running simulation
 */
export const pauseSimulation = async (planId: string): Promise<SimulationState> => {
  const response = await api.post<ApiResponse<SimulationState>>('/api/simulation/pause', {
    planId,
  });
  return response.data.data;
};

/**
 * Resume a paused simulation
 */
export const resumeSimulation = async (planId: string): Promise<SimulationState> => {
  const response = await api.post<ApiResponse<SimulationState>>('/api/simulation/resume', {
    planId,
  });
  return response.data.data;
};

/**
 * Change simulation speed
 */
export const setSimulationSpeed = async (
  planId: string,
  speed: 1 | 2 | 4 | 8
): Promise<SimulationState> => {
  const response = await api.post<ApiResponse<SimulationState>>('/api/simulation/speed', {
    planId,
    speed,
  });
  return response.data.data;
};

/**
 * Get current simulation state
 */
export const getSimulationState = async (planId: string): Promise<SimulationState | null> => {
  try {
    const response = await api.get<ApiResponse<SimulationState>>(
      `/api/simulation/${planId}/state`
    );
    return response.data.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Get simulation events for a plan
 */
export const getSimulationEvents = async (planId: string): Promise<SimulationEventData[]> => {
  const response = await api.get<ApiResponse<SimulationEventData[]>>(
    `/api/simulation/${planId}/events`
  );
  return response.data.data;
};

/**
 * Process simulation command
 */
export const processSimulationCommand = async (
  command: SimulationCommand
): Promise<SimulationState | void> => {
  const response = await api.post<ApiResponse<SimulationState | undefined>>(
    '/api/simulation/command',
    command
  );
  return response.data.data;
};
